import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { motion, useScroll, useSpring } from 'framer-motion';
import {
  TrendingUp,
  Users,
  DollarSign,
  Globe2,
  BarChart3,
  CheckCircle2,
  ArrowRight,
  Award,
  Target,
  Rocket,
  ShieldCheck,
  CreditCard,
  LayoutDashboard,
  Code2,
  Cloud,
  Database,
  Lock,
} from 'lucide-react';

const fadeUp = {
  hidden: { opacity: 0, y: 32 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] } },
};

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.08 } },
};

const Reveal: React.FC<React.PropsWithChildren<{ className?: string; delay?: number }>> = ({
  children,
  className,
  delay = 0,
}) => (
  <motion.div
    className={className}
    variants={fadeUp}
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.2 }}
    transition={{ delay }}
  >
    {children}
  </motion.div>
);

const KogitCaseStudy = () => {
  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 24, mass: 0.4 });

  const screenshots = [
    {
      src: '/kogit.png',
      title: 'Marketing Homepage',
      description: 'Conversion-focused landing experience showcasing the platform value proposition.',
    },
    {
      src: '/kogit2.png',
      title: 'Authentication Flow',
      description: 'Secure sign-in with Google OAuth and email/password, plus password recovery.',
    },
    {
      src: '/kogit3.png',
      title: 'Learner Dashboard',
      description: 'Personalised plan tracking, simulation timers and subscription management.',
    },
  ];

  const topPages = [
    { title: 'KogIT — Best Simulation for Computer Repair', views: '15K', engagement: '87%' },
    { title: 'KogIT — IT Simulation Platform for Real-World Skills', views: '4.8K', engagement: '72%' },
    { title: 'KogIT Dashboard — Your IT Learning Progress', views: '1.8K', engagement: '91%' },
    { title: 'KogIT Plans — Choose Your IT Learning Path', views: '735', engagement: '68%' },
  ];

  const stack = [
    { name: 'React + TypeScript', icon: Code2, group: 'Frontend' },
    { name: 'Tailwind CSS', icon: LayoutDashboard, group: 'Frontend' },
    { name: 'Node.js & Express', icon: Code2, group: 'Backend' },
    { name: 'Supabase', icon: Database, group: 'Backend' },
    { name: 'Google Cloud Platform', icon: Cloud, group: 'Infrastructure' },
    { name: 'Stripe Payments', icon: CreditCard, group: 'Infrastructure' },
    { name: 'Google OAuth', icon: Lock, group: 'Security' },
    { name: 'GA4 Analytics', icon: BarChart3, group: 'Insights' },
  ];

  const features = [
    { title: 'Multi-Provider Authentication', description: 'Email/password and Google OAuth with full session management and password recovery.', icon: ShieldCheck },
    { title: 'Interactive Course Dashboard', description: 'Real-time progress tracking, integrated video player and a frictionless learning loop.', icon: LayoutDashboard },
    { title: 'Subscription Billing', description: 'Stripe-backed plans with proration, dunning and self-serve subscription management.', icon: CreditCard },
    { title: 'Comprehensive Admin Panel', description: 'Content, user and revenue tooling for the operations team — built for non-engineers.', icon: Users },
    { title: 'Analytics & Tracking', description: 'GA4 with custom event taxonomy mapped to the funnel and retention loops.', icon: BarChart3 },
    { title: 'Scalable Infrastructure', description: 'Deployed on GCP with CI/CD, autoscaling and 99.9% uptime since launch.', icon: Cloud },
  ];

  return (
    <>
      <Helmet>
        <title>KogIt Case Study: From Zero to 1,500+ Users | Omnicraft</title>
        <meta
          name="description"
          content="Discover how we built and scaled KogIt to 1,500+ active users with 11 paying customers across 7 countries. A complete case study on educational platform development, user acquisition, and sustainable growth strategies."
        />
        <meta
          name="keywords"
          content="KogIt case study, educational platform development, SaaS growth case study, user acquisition strategy, edtech success story, learning management system, React application development, scalable web application, subscription-based platform, international user growth"
        />
        <link rel="canonical" href="https://www.omnicraftservices.com/case-study/kogit" />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://www.omnicraftservices.com/case-study/kogit" />
        <meta property="og:title" content="KogIt Success Story: From Zero to 1,500+ Users & Growing" />
        <meta property="og:description" content="Complete case study: How we built an educational platform that reached 1,500+ users and 11 paying customers with continuous growth." />
        <meta property="og:image" content="https://www.omnicraftservices.com/kogit.png" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://www.omnicraftservices.com/case-study/kogit" />
        <meta property="twitter:title" content="KogIt Success Story: From Zero to 1,500+ Users & Growing" />
        <meta property="twitter:description" content="Complete case study: How we built an educational platform that reached 1,500+ users and 11 paying customers with continuous growth." />
        <meta property="twitter:image" content="https://www.omnicraftservices.com/kogit.png" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Article',
            headline: 'KogIt Success Story: Educational Platform Case Study',
            description: 'How we built and scaled KogIt to 1,500+ active users with 11 paying customers',
            image: 'https://www.omnicraftservices.com/kogit.png',
            author: { '@type': 'Organization', name: 'OmniCraft Services' },
            publisher: {
              '@type': 'Organization',
              name: 'OmniCraft Services',
              logo: { '@type': 'ImageObject', url: 'https://www.omnicraftservices.com/logo.png' },
            },
            datePublished: '2024-11-30',
            dateModified: '2024-11-30',
          })}
        </script>
      </Helmet>

      <motion.div
        className="fixed top-0 left-0 right-0 h-1 bg-primary origin-left z-[60]"
        style={{ scaleX: progress }}
      />

      <div className="min-h-screen bg-background text-foreground">
        {/* Hero */}
        <section className="relative overflow-hidden pt-28 pb-20 md:pt-36 md:pb-28">
          <div className="absolute inset-0 bg-gradient-to-b from-primary/10 via-background to-background pointer-events-none" />
          <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] rounded-full bg-primary/15 blur-3xl pointer-events-none" />
          <div className="absolute -bottom-40 -left-32 w-[28rem] h-[28rem] rounded-full bg-omni-light-blue/15 blur-3xl pointer-events-none" />

          <div className="container-custom relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <motion.div
                initial="hidden"
                animate="visible"
                variants={stagger}
                className="space-y-6"
              >
                <motion.div variants={fadeUp}>
                  <Badge className="bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15">
                    <TrendingUp className="w-3 h-3 mr-1.5" />
                    Active growth · Live in production
                  </Badge>
                </motion.div>

                <motion.h1
                  variants={fadeUp}
                  className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]"
                >
                  KogIt: an edtech platform that
                  <span className="block text-primary mt-2">scaled from zero to 1,500+ users</span>
                </motion.h1>

                <motion.p variants={fadeUp} className="text-lg md:text-xl text-slate-600 dark:text-slate-300 max-w-xl leading-relaxed">
                  We designed, built and grew an IT-simulation learning platform for Learn Kogit Inc. — covering full-stack engineering,
                  payments, analytics and the marketing engine that took it international.
                </motion.p>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3 pt-2">
                  <Badge variant="outline" className="text-sm py-1.5 px-3 border-primary/30 text-foreground">
                    <Users className="w-4 h-4 mr-1.5 text-primary" /> 1,500+ users
                  </Badge>
                  <Badge variant="outline" className="text-sm py-1.5 px-3 border-primary/30 text-foreground">
                    <DollarSign className="w-4 h-4 mr-1.5 text-primary" /> 11 paid subscribers
                  </Badge>
                  <Badge variant="outline" className="text-sm py-1.5 px-3 border-primary/30 text-foreground">
                    <Globe2 className="w-4 h-4 mr-1.5 text-primary" /> 7 countries
                  </Badge>
                </motion.div>

                <motion.div variants={fadeUp} className="flex flex-wrap gap-3 pt-4">
                  <Button asChild size="lg" className="rounded-full">
                    <Link to="/contact" className="inline-flex items-center gap-2">
                      Start a similar project <ArrowRight className="w-4 h-4" />
                    </Link>
                  </Button>
                  <Button asChild size="lg" variant="outline" className="rounded-full border-2">
                    <Link to="/portfolio">More case studies</Link>
                  </Button>
                </motion.div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="relative"
              >
                <div className="absolute -inset-4 bg-gradient-to-tr from-primary/20 to-omni-light-blue/20 rounded-3xl blur-2xl" />
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 20 }}
                  className="relative rounded-2xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900"
                >
                  <div className="h-9 bg-slate-100 dark:bg-slate-800 flex items-center px-3 gap-1.5 border-b border-slate-200 dark:border-slate-700">
                    <div className="w-2.5 h-2.5 rounded-full bg-red-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-yellow-400" />
                    <div className="w-2.5 h-2.5 rounded-full bg-green-400" />
                    <div className="flex-1 ml-2 h-5 bg-white dark:bg-slate-700 rounded text-[10px] flex items-center px-2 text-slate-500">
                      learnkogit.com
                    </div>
                  </div>
                  <img src="/kogit.png" alt="KogIt platform" className="w-full h-auto block" />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.4, duration: 0.6 }}
                  className="hidden md:block absolute -bottom-10 -right-6 w-2/5 rounded-xl overflow-hidden shadow-2xl border border-slate-200 dark:border-slate-800"
                >
                  <img src="/kogit3.png" alt="KogIt dashboard" className="w-full h-auto block" />
                </motion.div>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Key metrics strip */}
        <section className="py-16 border-y border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-900/50">
          <div className="container-custom">
            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-6"
            >
              {[
                { value: '6.9K', label: 'Active users / 30d', icon: Users },
                { value: '25K', label: 'Page views', icon: BarChart3 },
                { value: '62K', label: 'Tracked events', icon: TrendingUp },
                { value: '3.7K', label: 'Engaged sessions', icon: Target },
              ].map((m) => (
                <motion.div key={m.label} variants={fadeUp} className="text-center md:text-left">
                  <div className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10 text-primary mb-3">
                    <m.icon className="w-5 h-5" />
                  </div>
                  <div className="text-3xl md:text-4xl font-bold tracking-tight">{m.value}</div>
                  <div className="text-sm text-slate-600 dark:text-slate-400 mt-1">{m.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Project Overview */}
        <section className="py-20">
          <div className="container-custom max-w-5xl">
            <Reveal>
              <div className="text-center mb-12">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">The brief</p>
                <h2 className="text-3xl md:text-4xl font-bold">A platform that had to scale before it had users</h2>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              <Reveal>
                <Card className="h-full border-2 border-slate-200 dark:border-slate-800 hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <Badge variant="outline" className="w-fit mb-2 border-primary/30 text-primary">Challenge</Badge>
                    <CardTitle className="text-xl">What Learn Kogit needed</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-slate-600 dark:text-slate-300">
                      {[
                        'Handle thousands of concurrent learners without buckling',
                        'Deliver an excellent UX on desktop and mobile from day one',
                        'Support a sustainable subscription business model',
                        'Reach a global audience with low-latency infrastructure',
                      ].map((t) => (
                        <li key={t} className="flex items-start gap-3">
                          <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Reveal>

              <Reveal delay={0.1}>
                <Card className="h-full border-2 border-slate-200 dark:border-slate-800 hover:border-primary/50 transition-colors">
                  <CardHeader>
                    <Badge variant="outline" className="w-fit mb-2 border-primary/30 text-primary">Solution</Badge>
                    <CardTitle className="text-xl">What we built</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-3 text-slate-600 dark:text-slate-300">
                      {[
                        'Modern React + TypeScript front-end backed by Node.js services',
                        'Supabase for data, GCP for compute, autoscaling from the start',
                        'Stripe-powered subscription billing with self-serve management',
                        'GA4 + custom events instrumented against the conversion funnel',
                      ].map((t) => (
                        <li key={t} className="flex items-start gap-3">
                          <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                          <span>{t}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <div className="grid md:grid-cols-3 gap-4 mt-6">
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Client</p>
                  <p className="font-semibold">Learn Kogit Inc.</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">New York, USA</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Timeline</p>
                  <p className="font-semibold">4 months build</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">+ ongoing growth</p>
                </div>
                <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400 mb-1">Sector</p>
                  <p className="font-semibold">EdTech / SaaS</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">IT skills simulation</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Platform Screenshots Gallery */}
        <section className="py-20 bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
          <div className="container-custom">
            <Reveal>
              <div className="text-center mb-14 max-w-2xl mx-auto">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">Inside the product</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">A guided tour of the platform</h2>
                <p className="text-slate-600 dark:text-slate-300">From first-touch marketing through authentication into the active learner experience.</p>
              </div>
            </Reveal>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid md:grid-cols-3 gap-6"
            >
              {screenshots.map((s, i) => (
                <motion.div
                  key={s.src}
                  variants={fadeUp}
                  whileHover={{ y: -8 }}
                  transition={{ type: 'spring', stiffness: 200, damping: 22 }}
                  className="group"
                >
                  <div className="rounded-xl overflow-hidden shadow-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950">
                    <div className="h-7 bg-slate-100 dark:bg-slate-800 flex items-center px-3 gap-1.5 border-b border-slate-200 dark:border-slate-700">
                      <div className="w-2 h-2 rounded-full bg-red-400" />
                      <div className="w-2 h-2 rounded-full bg-yellow-400" />
                      <div className="w-2 h-2 rounded-full bg-green-400" />
                    </div>
                    <div className="aspect-[16/10] overflow-hidden bg-slate-50 dark:bg-slate-900">
                      <img
                        src={s.src}
                        alt={s.title}
                        loading="lazy"
                        className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-105"
                      />
                    </div>
                  </div>
                  <div className="mt-4">
                    <p className="text-xs font-semibold uppercase tracking-wider text-primary">{`0${i + 1}`}</p>
                    <h3 className="font-semibold text-lg mt-1">{s.title}</h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">{s.description}</p>
                  </div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Tech Stack */}
        <section className="py-20">
          <div className="container-custom max-w-5xl">
            <Reveal>
              <div className="text-center mb-12">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">Stack</p>
                <h2 className="text-3xl md:text-4xl font-bold">Built with tools we'd ship to production again</h2>
              </div>
            </Reveal>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="grid grid-cols-2 md:grid-cols-4 gap-4"
            >
              {stack.map((s) => (
                <motion.div
                  key={s.name}
                  variants={fadeUp}
                  whileHover={{ y: -4 }}
                  className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-xl p-5 hover:border-primary/50 hover:shadow-md transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                    <s.icon className="w-5 h-5" />
                  </div>
                  <p className="font-semibold text-sm">{s.name}</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-0.5">{s.group}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Features delivered */}
        <section className="py-20 bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
          <div className="container-custom max-w-6xl">
            <Reveal>
              <div className="text-center mb-12 max-w-2xl mx-auto">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">What we shipped</p>
                <h2 className="text-3xl md:text-4xl font-bold">Six pillars holding up the product</h2>
              </div>
            </Reveal>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.15 }}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-5"
            >
              {features.map((f) => (
                <motion.div key={f.title} variants={fadeUp} whileHover={{ y: -4 }}>
                  <Card className="h-full border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:shadow-lg transition-all">
                    <CardContent className="pt-6">
                      <div className="w-11 h-11 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-4">
                        <f.icon className="w-5 h-5" />
                      </div>
                      <h3 className="font-semibold text-lg mb-2">{f.title}</h3>
                      <p className="text-sm text-slate-600 dark:text-slate-300 leading-relaxed">{f.description}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Live Analytics */}
        <section className="py-20">
          <div className="container-custom max-w-6xl">
            <Reveal>
              <div className="text-center mb-12 max-w-2xl mx-auto">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">Live analytics</p>
                <h2 className="text-3xl md:text-4xl font-bold mb-3">Real numbers, pulled straight from GA4</h2>
                <p className="text-slate-600 dark:text-slate-300">Last-30-day snapshot — no edits, no smoothing, no vanity metrics.</p>
              </div>
            </Reveal>

            <Reveal>
              <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
                <img
                  src="/kogitstatschart.png"
                  alt="KogIt GA4 dashboard — 6.9K active users, 25K page views, 62K events"
                  className="w-full h-auto block"
                  loading="lazy"
                />
              </div>
            </Reveal>

            <Reveal delay={0.1}>
              <div className="grid md:grid-cols-2 gap-6 mt-8">
                <Card className="border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Top performing pages</CardTitle>
                    <CardDescription>Most viewed pages by users</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {topPages.map((p, idx) => (
                        <div key={idx} className="flex items-center justify-between gap-3 py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                          <p className="text-sm text-slate-700 dark:text-slate-300 line-clamp-1">{p.title}</p>
                          <div className="flex items-center gap-3 flex-shrink-0">
                            <span className="text-sm font-semibold">{p.views}</span>
                            <span className="text-xs px-2 py-0.5 rounded-full bg-primary/10 text-primary font-medium">{p.engagement}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Where users come from</CardTitle>
                    <CardDescription>Active users by country, last 30 days</CardDescription>
                  </CardHeader>
                  <CardContent className="p-0 overflow-hidden">
                    <img
                      src="/kogitcountrieschart.png"
                      alt="Active users by country: US 2K, China 422, Philippines 415, Indonesia 398, Germany 239, South Africa 200, Malaysia 168"
                      className="w-full h-auto block"
                      loading="lazy"
                    />
                  </CardContent>
                </Card>
              </div>
            </Reveal>
          </div>
        </section>

        {/* Growth Strategy */}
        <section className="py-20 bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
          <div className="container-custom max-w-5xl">
            <Reveal>
              <div className="text-center mb-12">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">Growth playbook</p>
                <h2 className="text-3xl md:text-4xl font-bold">How we turned a launch into compounding traction</h2>
              </div>
            </Reveal>

            <motion.div
              variants={stagger}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              className="grid md:grid-cols-3 gap-5"
            >
              {[
                { icon: BarChart3, title: 'SEO foundation', body: 'Technical and on-page SEO — 40% of organic traffic from search.' },
                { icon: Users, title: 'Engagement loops', body: 'Retention-led product decisions drove a 60% monthly active rate.' },
                { icon: Rocket, title: 'Funnel discipline', body: 'Tight free-to-paid funnel with a 0.73% conversion at this stage.' },
              ].map((g) => (
                <motion.div key={g.title} variants={fadeUp} whileHover={{ y: -6 }}>
                  <Card className="h-full text-center border-slate-200 dark:border-slate-800 hover:border-primary/50 hover:shadow-lg transition-all">
                    <CardHeader>
                      <div className="w-14 h-14 mx-auto rounded-2xl bg-primary/10 text-primary flex items-center justify-center mb-2">
                        <g.icon className="w-6 h-6" />
                      </div>
                      <CardTitle>{g.title}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-slate-600 dark:text-slate-300">{g.body}</p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </section>

        {/* Results */}
        <section className="py-20">
          <div className="container-custom max-w-5xl">
            <Reveal>
              <div className="text-center mb-12">
                <p className="text-sm font-semibold uppercase tracking-wider text-primary mb-2">Outcomes</p>
                <h2 className="text-3xl md:text-4xl font-bold">Results & business impact</h2>
              </div>
            </Reveal>

            <div className="grid md:grid-cols-2 gap-6">
              <Reveal>
                <Card className="border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">User metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      ['Total users', '1,500+'],
                      ['Paying customers', '11'],
                      ['Countries reached', '7'],
                      ['Monthly active users', '~900'],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <span className="text-slate-600 dark:text-slate-400">{k}</span>
                        <span className="font-bold text-lg">{v}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </Reveal>

              <Reveal delay={0.1}>
                <Card className="border-slate-200 dark:border-slate-800">
                  <CardHeader>
                    <CardTitle className="text-lg">Growth metrics</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {[
                      ['User growth rate', '+468%'],
                      ['Conversion rate', '0.73%'],
                      ['Platform uptime', '99.9%'],
                      ['User retention', '60%'],
                    ].map(([k, v]) => (
                      <div key={k} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 last:border-0">
                        <span className="text-slate-600 dark:text-slate-400">{k}</span>
                        <span className="font-bold text-lg text-primary">{v}</span>
                      </div>
                    ))}
                  </CardContent>
                </Card>
              </Reveal>
            </div>

            <Reveal delay={0.15}>
              <Card className="mt-6 bg-primary/5 border-primary/20">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <Award className="w-7 h-7 text-primary" />
                    <CardTitle>Key achievements</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <ul className="grid md:grid-cols-2 gap-x-8 gap-y-3">
                    {[
                      'Scaled from 0 → 1,500+ users in under 12 months',
                      'Sustainable revenue with 11 paying customers and growing',
                      '99.9% platform uptime since launch — zero critical incidents',
                      'Active users across 7 countries with minimal paid spend',
                      'Highly engaged community with 60% monthly retention',
                      'Infrastructure ready to absorb 10× the current load',
                    ].map((a) => (
                      <li key={a} className="flex items-start gap-2.5">
                        <CheckCircle2 className="w-4 h-4 text-primary mt-1 flex-shrink-0" />
                        <span className="text-sm text-slate-700 dark:text-slate-300">{a}</span>
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            </Reveal>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-20 bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
          <div className="container-custom max-w-3xl">
            <Reveal>
              <div className="text-center">
                <div className="text-6xl text-primary/40 leading-none mb-2">"</div>
                <blockquote className="text-xl md:text-2xl font-medium text-slate-700 dark:text-slate-200 italic leading-relaxed">
                  The platform exceeded our expectations in every way. The team delivered a scalable, high-performance
                  solution that has become the foundation of our business growth.
                </blockquote>
                <div className="mt-8 inline-block pt-4 border-t border-slate-200 dark:border-slate-700">
                  <p className="font-semibold">Learn Kogit Inc.</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">Educational Technology · New York, USA</p>
                </div>
              </div>
            </Reveal>
          </div>
        </section>

        {/* CTA */}
        <section className="py-24 bg-primary text-primary-foreground relative overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-white/10 rounded-full blur-3xl" />
          <div className="container-custom max-w-3xl text-center relative z-10">
            <Reveal>
              <h2 className="text-3xl md:text-5xl font-bold mb-5">Ready to build your success story?</h2>
              <p className="text-lg md:text-xl mb-10 opacity-90">
                We design, ship and grow products like KogIt — from first wireframe to scaling traction.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Button asChild size="lg" variant="secondary" className="rounded-full min-w-[200px]">
                  <Link to="/contact" className="inline-flex items-center gap-2">
                    Start your project <ArrowRight className="w-4 h-4" />
                  </Link>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="rounded-full min-w-[200px] bg-transparent border-2 border-white text-white hover:bg-white hover:text-primary"
                >
                  <Link to="/portfolio">View more projects</Link>
                </Button>
              </div>
            </Reveal>
          </div>
        </section>
      </div>
    </>
  );
};

export default KogitCaseStudy;
